import React, { useRef, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Space, Button, Segmented } from 'antd';
import DataTable, { type DataTableRef } from '../../components/Charts/DataTable';
import Dashboard from '../../components/Templates/Dashboard';
import type { FieldConfig } from '../../components/ForgeQueryBuilder';
import { AnalysisItem, overdueApi } from '../../services/statistics/overdue';
import { useAuditors, useShopMap } from '../../services/store/organization.store';
// import { getCommonOverdueQueryFields, convertOverdueQueryToParams } from '../statistics/overdue/BaseAnalysis';
import { FileExcelOutlined } from '@ant-design/icons';
import { getDefaultQuery, getKpiStatic } from './effect';



type Row = AnalysisItem;

const badgeStyleOfNumber = (percent: number): { backgroundColor?: string; color?: string } => {
  if (percent === 0) return { backgroundColor: '#d9f7be', color: '#237804' };
  if (percent >= 5) return { backgroundColor: '#ffccc7', color: '#a8071a' };
  return { backgroundColor: '#fff1b8', color: '#ad6800' };
};

type RatioCellRenderer = (numerator: number, mergeTotal: number) => React.ReactElement;

function buildDeviceColumns(renderRatioCell: RatioCellRenderer): ColumnsType<Row> {
  return [
    { title: '订单所处期数', dataIndex: 'period', key: 'period', fixed: 'left', width: 120, render: (_: unknown, r: Row) => `M${r.period - 1}` },
    { title: '订单总计', key: 'total', width: 140, align: 'center', render: (_: unknown, r: Row) => renderRatioCell(r.overdue.total, r.origin.total) },
    { title: '全新苹果', key: 'deviceNewApple', width: 140, align: 'center', render: (_: unknown, r: Row) => renderRatioCell(r.overdue.deviceNewApple, r.origin.deviceNewApple) },
    { title: '二手苹果', key: 'deviceSecondApple', width: 140, align: 'center', render: (_: unknown, r: Row) => renderRatioCell(r.overdue.deviceSecondApple, r.origin.deviceSecondApple) },
    { title: '全新小米', key: 'deviceNewXiaomi', width: 140, align: 'center', render: (_: unknown, r: Row) => renderRatioCell(r.overdue.deviceNewXiaomi, r.origin.deviceNewXiaomi) },
    { title: '全新华为', key: 'deviceNewHuawei', width: 140, align: 'center', render: (_: unknown, r: Row) => renderRatioCell(r.overdue.deviceNewHuawei, r.origin.deviceNewHuawei) },
    { title: '全新VIVO', key: 'deviceNewVivo', width: 140, align: 'center', render: (_: unknown, r: Row) => renderRatioCell(r.overdue.deviceNewVivo, r.origin.deviceNewVivo) },
  ];
}

function buildRiskColumns(renderRatioCell: RatioCellRenderer): ColumnsType<Row> {
  return [
    { title: '订单所处期数', dataIndex: 'period', key: 'period', fixed: 'left', width: 120, render: (_: unknown, r: Row) => `M${r.period - 1}` },
    { title: '订单总计', key: 'total', width: 140, align: 'center', render: (_: unknown, r: Row) => renderRatioCell(r.overdue.total, r.origin.total) },
    { title: '盲发', key: 'riskBlindShip', width: 140, align: 'center', render: (_: unknown, r: Row) => renderRatioCell(r.overdue.riskBlindShip, r.origin.riskBlindShip) },
    { title: '通过', key: 'riskPass', width: 140, align: 'center', render: (_: unknown, r: Row) => renderRatioCell(r.overdue.riskPass, r.origin.riskPass) },
    { title: '预收', key: 'riskPrePay', width: 140, align: 'center', render: (_: unknown, r: Row) => renderRatioCell(r.overdue.riskPrePay, r.origin.riskPrePay) },
    { title: '高风险', key: 'riskHigh', width: 140, align: 'center', render: (_: unknown, r: Row) => renderRatioCell(r.overdue.riskHigh, r.origin.riskHigh) },
  ];
}

interface Props {
  metric?: 'count' | 'amount' | 'bad_debt_count' | 'bad_debt_amount';
  title?: string;
}

const OverdueDataStatistics: React.FC<Props> = ({ metric = 'count', title = '逾期数据统计' }) => {
  const { shopList } = useShopMap();
  const tableRef = useRef<DataTableRef>(null);
  const [viewMode, setViewMode] = useState<'device' | 'risk'>('device');
  const { auditorList } = useAuditors();  
  const fields: FieldConfig[] = [
      { key: 'orderCreatedAt',label: '接单日期', type: 'date', operators: ['between'], format: 'YYYY-MM-DD', placeholder: ['开店日期', '至今'] },
      // { key: 'auditorId', label: '审核人', type: 'select', getOptions: () => [
      //   ...auditorList.map(a => ({ label: a.name, value: a.key }))
      // ] },
      {
        key: 'storeId', label: '店铺', type: 'select', operators: ['eq'],
        options: shopList.map(shop => ({ label: `${shop.name}`, value: Number(shop.code) }))
      },
    ];

  return (
    <Dashboard
      title={title}
      queryFields={fields}
      fetcher={async (queryParams) => {
        const conv = queryParams
        const filter = conv.orderFilter || {};
        const [resp, stats] = await Promise.all([
          overdueApi.getOrderAggregateAnalysis({ filter, metric }),
          overdueApi.getOverdueSummary(queryParams as any),
        ]);
        return { rows: resp.data, stats };
      }}
      defaultQuery={getDefaultQuery()}
      kpiItems={getKpiStatic as any}
      sections={[{
        key: 'overdue-data-statistics-table',
        title: '逾期数据总览',
        extra: (
          <Space>
            <Segmented 
              options={[{ label: '设备类型', value: 'device' }, { label: '风控类型', value: 'risk' }]} 
              value={viewMode} 
              onChange={(v) => setViewMode(v as 'device' | 'risk')} 
            />
            <Button icon={<FileExcelOutlined />} onClick={() => tableRef.current?.exportCSV({ filename: 'overdue-data-statistics.csv' })}>导出Excel</Button>
          </Space>
        ),
        render: ({ rows }) => {
          const renderCell: RatioCellRenderer = (numerator, mergeTotal) => {
            const total = mergeTotal || 0;
            const percent = total > 0 ? (numerator / total) * 100 : 0;
            const style = badgeStyleOfNumber(Number(percent.toFixed(2)));
            const isAmount = metric === 'amount' || metric === 'bad_debt_amount';
            const numText = isAmount ? `¥${Number(numerator).toFixed(2)}` : `${Math.round(numerator)}`;
            const totalText = isAmount ? `¥${Number(total).toFixed(2)}` : `${Math.round(total)}`;
            return (
              <div style={{ lineHeight: 1.4 }}>
                <div>{`${numText} / ${totalText}`}</div>
                <div style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 4, marginTop: 2, ...style }}>{`${percent.toFixed(2)}%`}</div>
              </div>
            );
          };
          return (
            <DataTable
              columns={viewMode === 'risk' ? buildRiskColumns(renderCell) : buildDeviceColumns(renderCell)}
              dataSource={rows as any}
              rowKey={(r: any) => String(r.period)}
              ref={tableRef as any}
            />
          );
        }
      }]}
    />
  );
};

export default OverdueDataStatistics;
